import type { PointData } from 'pixi.js';
import { Signal } from 'typed-signals';
import type {
  GameTutorialBoosterButtonLockStep,
  GameTutorialBoosterUseStep,
  GameTutorialGoalStep,
  GameTutorialMoveStep,
  GameTutorialStep,
  OpenBlockerIntroPopupStep,
  OpenBoosterPopupStep,
} from './types';
import { GameTutorialStepType } from './types';
import type { GameTutorialView } from './GameTutorialView';
import { EventScheduler } from '../../../utils/EventScheduler';
import type { GameTutorialModel } from './GameTutorialModel';
import type { BoardModel } from '../../boardMVC/models/board/BoardModel';
import { BoardEventType } from '../../boardMVC/models/board/BoardEvents';
import type { BlockerIntroType } from '../../blockers/constants';
import type { TileStackModel } from '../../boardMVC/models/board/TileStackModel';
import type { BoosterType } from '../../boosters/types';

export interface GameTutorialControllerConfig {
  model: GameTutorialModel;
  view: GameTutorialView;
  boardModel: BoardModel;
  openBoosterPopup(boosterType: BoosterType, collect: boolean): Promise<void>;
  useBooster(boosterType: BoosterType): Promise<void>;
  openBlockerPopup: (blockerType: BlockerIntroType) => Promise<void>;
}

export class GameTutorialController {
  public readonly onFinished = new Signal();

  private readonly scheduler = new EventScheduler();

  private readonly lockedButtons: Partial<Record<BoosterType, boolean>> = {};

  constructor(private readonly config: GameTutorialControllerConfig) {
    this.config.model.getSetupSteps().forEach((step) => {
      void this.executeStep(step);
    });
  }

  public start(): void {
    void this.setNextStep();
  }

  private async setNextStep(): Promise<void> {
    const { model, view } = this.config;
    const prevStep = model.getActiveStep();
    const nextStep = model.setNextStep();

    if (prevStep) {
      view.clear();
    }

    if (nextStep) {
      await this.executeStep(nextStep);
      void this.setNextStep();
    } else {
      this.onFinished.emit();
    }
  }

  private async executeStep(step: GameTutorialStep): Promise<void> {
    const {
      config: { view, boardModel, openBoosterPopup, useBooster, openBlockerPopup },
      scheduler,
    } = this;

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    const { promise, resolve } = Promise.withResolvers<void>();

    if (step) {
      if (step.type === GameTutorialStepType.WaitForTileRemoval) {
        const onTileRemoved = (): void => {
          scheduler.addEvent(() => {
            resolve();
          }, 250);
          boardModel.events.removeListener(BoardEventType.TileRemovedFinished, onTileRemoved);
        };
        boardModel.events.addListener(BoardEventType.TileRemovedFinished, onTileRemoved);
      } else if (step.type === GameTutorialStepType.Move) {
        const movementStep = step as GameTutorialMoveStep;
        view.showMovementStep(movementStep);

        const callback = (): void => {
          if (view.isMovementStepComplete(movementStep)) {
            view.endMovementStep(movementStep);
          }

          resolve();
          boardModel.events.removeListener(BoardEventType.MoveMade, callback);
        };

        boardModel.events.addListener(BoardEventType.MoveMade, callback);
      } else if (step.type === GameTutorialStepType.Goal) {
        const goalStep = step as GameTutorialGoalStep;
        scheduler.addEvent(() => {
          view.showGoalStep(goalStep, () => {
            scheduler.addEvent(() => {
              resolve();
            }, 250);
          });
        }, 250);
      } else if (step.type === GameTutorialStepType.BoosterUse) {
        const boosterStep = step as GameTutorialBoosterUseStep;
        await useBooster(boosterStep.boosterType);
        resolve();
      } else if (step.type === GameTutorialStepType.OpenBoosterPopup) {
        const openPopupStep = step as OpenBoosterPopupStep;
        await openBoosterPopup(openPopupStep.boosterType, openPopupStep.collect);
        resolve();
      } else if (step.type === GameTutorialStepType.SetBoosterButtonLock) {
        const buttonLockStep = step as GameTutorialBoosterButtonLockStep;
        this.lockedButtons[buttonLockStep.boosterType] = buttonLockStep.locked;
        resolve();
      } else if (step.type === GameTutorialStepType.OpenBlockerIntroPopup) {
        const openBlockerIntroPopupStep = step as OpenBlockerIntroPopupStep;
        await openBlockerPopup(openBlockerIntroPopupStep.blockerIntroType);
        resolve();
      }
    }

    return promise;
  }

  public canPlaceStack(stack: TileStackModel): boolean {
    const step = this.config.model.getActiveStep();
    if (!step) return true;

    if (step.type === GameTutorialStepType.WaitForTileRemoval) return true;

    if (step.type === GameTutorialStepType.Move) {
      const movementStep = step as GameTutorialMoveStep;
      return movementStep.stackX === stack.x && movementStep.stackY === stack.y;
    }

    return false;
  }

  public canUseBooster(type: BoosterType): boolean {
    const step = this.config.model.getActiveStep();
    if (!step) return true;

    if (step.type !== GameTutorialStepType.BoosterUse) return true;

    return type === (step as GameTutorialBoosterUseStep).boosterType;
  }

  public isBoosterButtonLocked(type: BoosterType): boolean {
    return this.lockedButtons[type] ?? false;
  }

  public canUseBoosterOnStack(stack: TileStackModel): boolean {
    const step = this.config.model.getActiveStep();
    if (!step) return true;

    if (step.type !== GameTutorialStepType.BoosterUse) return true;

    const { availableStacks } = step as GameTutorialBoosterUseStep;

    if (availableStacks.length === 0) return true;

    return availableStacks.some(({ x, y }) => x === stack.x && y === stack.y);
  }

  public getAvailableStacksForBooster(): PointData[] {
    const step = this.config.model.getActiveStep();
    if (!step) return [];

    if (step.type !== GameTutorialStepType.BoosterUse) return [];

    return (step as GameTutorialBoosterUseStep).availableStacks;
  }

  public canRemoveFromTray(stack: TileStackModel): boolean {
    const step = this.config.model.getActiveStep();
    if (!step) return true;

    if (step.type === GameTutorialStepType.WaitForTileRemoval) return true;

    if (step.type === GameTutorialStepType.Move) {
      const movementStep = step as GameTutorialMoveStep;
      return movementStep.trayX === stack.x;
    }

    return false;
  }

  public update(deltaTime: number): void {
    this.scheduler.update(deltaTime);
    this.config.view.update(deltaTime);
  }

  public destroy(): void {
    this.config.view.destroy();
  }
}
